/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React, { useEffect, useCallback, useState, useContext } from 'react';
import { IconNames } from '@blueprintjs/icons';
import { Button, H6, Icon, InputGroup, Tree, ITreeNode, Intent, Dialog, Classes } from '@blueprintjs/core';
import { DIL, selectUserName } from 'srs-middleware';
import { deleteDefinition, uploadDilId } from './utils';

import { DatasetManagementContext } from './datasetManagementContext';
// import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
// import { WorkspaceContext } from '/views/workspace/workspaceContext';

export const SidePanel = () => {
	const styles = panelStyles;

	const {dilList, setSelectedPanelDil, selectedPanelDil, 
		toaster, setNeedUpdate, isUpdating, unsavedChanges} = useContext(DatasetManagementContext);
	const [sortedDils, setSortedDils] = useState<DilTreeNode[]>();
	const [searchFilter, setSearchFilter] = useState<string>('');
	const [dilDefinitionIdToDelete, setDilDefinitionIdToDelete] = useState<number>();

	const getDilName = useCallback((dilDefnitionId: number) => {
		return sortedDils?.filter(d => d.dilDefinitionId === dilDefnitionId)[0]?.name || "";
	},[sortedDils]);

	const onDeleteClick = useCallback(
		(e: React.MouseEvent, dilDefinitionId: number) => {
			e.stopPropagation(); //stop propagation, so we don't select the dil
			setDilDefinitionIdToDelete(dilDefinitionId);
	}, [setDilDefinitionIdToDelete]);

	const deleteDil = useCallback(() => {
		if (dilDefinitionIdToDelete) {
			deleteDefinition(dilDefinitionIdToDelete).then(() => {
				setDilDefinitionIdToDelete(null);
				toaster.show({
					icon: 'tick-circle',
					message: `Dataset ${getDilName(dilDefinitionIdToDelete) || "" } Deleted`,
					intent: Intent.SUCCESS,
				});
				setNeedUpdate(true); 
				window.location.reload(false);
			});
		}
	}, [toaster, dilDefinitionIdToDelete, getDilName]); //dispatch, forceRefresh

	useEffect(() => {
		let dilValues: DilTreeNode[] = Object.values(dilList);
				
		setSortedDils(
			dilValues
				// verifying it is a dataset uploaded by the user vs. through a pipeline
				.filter((dil: DIL) => dil.dilId === uploadDilId) 
				// verifying it is a dataset that the current user owns (can't update a dataset not uploaded by you)
				.filter((dil: DIL) => Object.keys(dil).includes('owner') && dil['owner'] === selectUserName()) 
				.filter((dil: DIL) => !searchFilter || dil.name.includes(searchFilter))
				.map((dil: DIL) => {
					return ({
					...dil,
					id: dil.dilDefinitionId,
					label: dil.name,
					isSelected: dil.dilDefinitionId === selectedPanelDil?.dilDefinitionId,
					secondaryLabel: (
						<Button
							icon="trash"
							minimal={true}
							onClick={(e: React.MouseEvent) => onDeleteClick(e, dil.dilDefinitionId)}
						/>
					),
				})})
		);
	}, [dilList, searchFilter, onDeleteClick, selectedPanelDil]);

	const handleNodeSelected = useCallback(
		(selectedDil: DilTreeNode) => {
			if (unsavedChanges) return;
			setSelectedPanelDil(selectedDil);
	}, [setSelectedPanelDil, unsavedChanges]);

	return (
		<div css={styles.container}>
			<div css={styles.header}>
				<Icon icon={IconNames.SETTINGS} css={styles.settingsIcon} />
				<div>
					<H6 css={styles.headerText}>Manage Data</H6>
					<div css={styles.text}>Edit, delete, and search your uploaded data.</div>
					<div css={styles.text}>Upload new data.</div>
				</div>
			</div>

			<div css={styles.buttonRow}>
				{
					selectedPanelDil
					?<Button icon={IconNames.ADD} minimal css={styles.button} onClick={() => setSelectedPanelDil()}>Upload Data</Button>
					: <div></div>
				}
			</div>
			<InputGroup
				css={styles.search}
				leftIcon={IconNames.SEARCH}
				placeholder={'Search'}
				onChange={(e) => setSearchFilter(e.target.value)}
			/>
			<Tree contents={sortedDils} css={styles.tree} onNodeClick={handleNodeSelected} />
			<Dialog
				autoFocus={true}
				canEscapeKeyClose={true}
				canOutsideClickClose={true}
				isOpen={!!dilDefinitionIdToDelete}
				title="Are you sure?"
				onClose={() => setDilDefinitionIdToDelete(null)}
			>
				<div className={Classes.DIALOG_BODY}>{`You're about to delete ${getDilName(dilDefinitionIdToDelete)}. Is that what you want?`}</div>

				<div className={Classes.DIALOG_FOOTER}>
					<div className={Classes.DIALOG_FOOTER_ACTIONS}>
						<Button onClick={() => setDilDefinitionIdToDelete(null)}>Whoops, nope.</Button>
						<Button onClick={deleteDil} intent={Intent.PRIMARY}>
							Yes, blow it away!
						</Button>
					</div>
				</div>
			</Dialog>
		</div>
	);
};

interface DilTreeNode extends DIL, ITreeNode {}

const panelStyles = {
	container: css`
		border-radius: 3px;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	`,
	header: css`
		padding: 20px 0 10px;
		background-color: rgb(235, 241, 245);
		display: flex;
	`,
	settingsIcon: css`
		color: #5c7080;
		margin-left: 20px;
		margin-right: 10px;
	`,
	headerText: css`
		margin: 0 0 5px;
	`,
	text: css`
		font-size: 12px;
	`,
	buttonRow: css`
		display: flex;
		width: 100%;
		justify-content: flex-end;
		min-height: 30px;
	`,
	button: css`
		color: #006699 !important;
		.bp3-icon:first-of-type {
			color: #006699 !important;
		}
	`,
	search: css`
		margin-left: 10px;
		margin-right: 10px;
	`,
	tree: css`
		overflow: auto;
		.bp3-tree-node-selected > .bp3-tree-node-content {
			background-color: rgb(235, 241, 245);
			color: rgb(19, 124, 189);
			.bp3-tree-node-icon, :hover button > .bp3-icon {
				color: rgb(19, 124, 189);
			}
			.bp3-tree-node-secondary-label > .bp3-icon {
				color: rgb(92, 112, 128);
			}
		}
		.bp3-tree-node{
			button{
				display: none;
			}
			:hover button{
				display: inline-flex;
			}
		}

	`,
};
